import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import InquiryBanner from './InquiryBanner.jsx';
import banner from '../assets/banners/im1.avif';
import { FiCheckCircle, FiShield, FiSettings, FiTrendingUp, FiAward, FiZap, FiDroplet } from 'react-icons/fi';
import p7 from '../assets/products/floating-valves.jpeg';
import '../css/floatingBallValves.css';

const keyFeatures = [
  {
    title: 'Floating Ball Design',
    description: 'The ball floats between the seats, ensuring tight sealing in both directions with minimal operating torque.'
  },
  {
    title: 'Low Torque Operation',
    description: 'Floating ball design requires lower operating torque compared to trunnion-mounted valves, making manual operation easier.'
  },
  {
    title: 'Bidirectional Sealing',
    description: 'Excellent sealing capabilities in both flow directions, providing reliable shut-off performance in various applications.'
  },
  {
    title: 'Compact Construction',
    description: 'Streamlined design with reduced face-to-face dimensions, ideal for space-constrained installations.'
  }
];

const specifications = [
  {
    icon: <FiSettings />,
    label: 'Size Range',
    value: '1/2" – 12"',
    detail: 'Standard sizes from half inch to 12 inches'
  },
  {
    icon: <FiTrendingUp />,
    label: 'Pressure Rating',
    value: 'Class 150 – 600',
    detail: 'Suitable for various pressure applications'
  },
  {
    icon: <FiShield />,
    label: 'Materials',
    value: 'Carbon Steel, Stainless Steel, Bronze',
    detail: 'Multiple material options for different services'
  },
  {
    icon: <FiAward />,
    label: 'Standards',
    value: 'API 6D, BS EN 593',
    detail: 'Compliant with international standards'
  }
];

const valveTypes = [
  {
    name: 'Full Port Floating Ball',
    description: 'Full bore design with minimal flow restriction, ideal for applications requiring maximum flow capacity.',
    features: ['Zero pressure drop', 'Full flow capacity', 'Easy cleaning', 'CIP capability']
  },
  {
    name: 'Reduced Port Floating Ball',
    description: 'Standard port design with reduced bore, providing cost-effective solution for general-purpose applications.',
    features: ['Cost-effective', 'Standard flow', 'Compact size', 'Versatile applications']
  },
  {
    name: 'Two-Piece Floating Ball',
    description: 'Two-piece body design with bolted connection, allowing easy maintenance and seat replacement.',
    features: ['Easy maintenance', 'Seat replacement', 'Bolted body', 'Service-friendly']
  },
  {
    name: 'Three-Piece Floating Ball',
    description: 'Three-piece body design with end connections, providing maximum flexibility for installation and maintenance.',
    features: ['Maximum flexibility', 'Easy installation', 'Inline maintenance', 'End connections']
  }
];

const advantages = [
  {
    icon: <FiZap />,
    title: 'Cost-Effective Solution',
    description: 'Floating ball valves offer an economical solution for low to medium pressure applications without compromising on performance and reliability.'
  },
  {
    icon: <FiShield />,
    title: 'Simple Operation',
    description: 'Floating ball design provides simple and reliable operation with lower torque requirements, suitable for both manual and automated applications.'
  },
  {
    icon: <FiSettings />,
    title: 'Versatile Applications',
    description: 'Suitable for a wide range of applications including water, oil, gas, and general industrial services up to medium pressure ratings.'
  },
  {
    icon: <FiDroplet />,
    title: 'Excellent Sealing',
    description: 'Floating ball mechanism ensures excellent sealing performance with bubble-tight shut-off capabilities in both directions.'
  }
];

const applications = [
  {
    title: 'Water Treatment',
    description: 'Municipal and industrial water treatment facilities requiring reliable shut-off valves for process control.'
  },
  {
    title: 'Oil & Gas Production',
    description: 'Upstream and midstream oil and gas applications requiring cost-effective ball valve solutions for flow control.'
  },
  {
    title: 'Chemical Processing',
    description: 'Chemical processing plants requiring versatile ball valves for various chemical services and process lines.'
  },
  {
    title: 'Power Generation',
    description: 'Power generation facilities where reliable ball valves are needed for water, steam, and auxiliary systems.'
  },
  {
    title: 'General Industry',
    description: 'General industrial applications requiring reliable shut-off valves for process control and system isolation.'
  },
  {
    title: 'HVAC Systems',
    description: 'Heating, ventilation, and air conditioning systems where compact ball valves are needed for flow control.'
  }
];

const FloatingBallValves = () => {
  const sectionsRef = useRef([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fbv-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current;
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header />
      
      {/* Hero Banner Section */}
      <section className="fbv-hero">
        <div className="fbv-hero-background">
          <img
            src={banner}
            alt="Floating Ball Valves Banner"
            className="fbv-hero-image"
          />
          <div className="fbv-hero-overlay"></div>
        </div>
        <div className="fbv-hero-content">
          <h1 className="fbv-hero-title">Floating Ball Valves</h1>
          <p className="fbv-hero-subtitle">
            Cost-effective and reliable floating ball valves designed for versatile industrial applications
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="fbv-section fbv-intro"
      >
        <div className="fbv-container">
          <div className="fbv-intro-grid">
            <div className="fbv-intro-text">
              <h2 className="fbv-intro-title">Floating Ball Valve</h2>
              <p className="fbv-intro-description">
                ANU Flow Control's floating ball valves are engineered for reliable performance in 
                low to medium pressure applications. Our valves feature a floating ball design where 
                the ball is free to move between the seats, ensuring tight sealing in both directions 
                with minimal operating torque. This design makes floating ball valves an economical 
                and versatile solution for a wide range of industrial applications.
              </p>
              <p className="fbv-intro-description">
                Available in sizes from 1/2" to 12" with pressure ratings from Class 150 to 600, our 
                floating ball valves are manufactured to API 6D and BS EN 593 standards. We offer 
                full port and reduced port designs, two-piece and three-piece configurations, and 
                various material options to meet diverse application requirements across industries.
              </p>
            </div>
            <div className="fbv-intro-image">
              <img src={p7} alt="Floating Ball Valves" className="fbv-intro-img" />
            </div>
          </div>
        </div>
      </div>

      {/* Specifications Section */}
      
      
     

     
      

      <InquiryBanner />
      <Footer />

      {scrollY > 300 && (
        <button 
          className="fbv-scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default FloatingBallValves;








